import React, { useMemo } from 'react';

import { Button, Grid, Section } from '@gogame/components';
import { FormField, withForm, getIn, SidePageV2 } from '@gogame/form';
import { useMountedState } from '@gogame/hooks-ts';
import Permissions from '@gogame/permissions';

import { loadBudget } from 'scenes/IO/sources/budget';
import {
  loadBusinessPartners,
  loadBusinessPartnerVersion,
  loadBusinessEntityDetails,
} from 'scenes/IO/sources/businessEntities';
import { loadParagraphIndication } from 'scenes/IO/sources/fields';
import { checkOnExistingIo } from 'scenes/IO/sources/io';

import AffiliatesField, { hasAffiliates } from './AffiliatesField';
import Agreement from './Agreement';
import Budget from './Budget';
import BusinessPartnerDetails from './BusinessPartnerDetails';
import IOSFields from './IOs/IOSFields';
import IOSWarningHierarchy from './IOs/IOSWarningHierarchy';
import LegalContact from './LegalContact';
import PaymentDetails from './PaymentDetails';
import PaymentTerms from './PaymentTerms';
import {
  PERIOD,
  BUSINESS_PARTNER,
  BUSINESS_PARTNER_VERSION,
  AGREEMENT,
  DESCRIPTION,
  BUDGET,
  PAYMENT_TERMS,
  CURRENCY_CODE,
  PAYMENT_DETAILS,
  BUSINESS_PARTNER_CONTACT_DETAILS,
  LEGAL_CONTACT,
  AFFECTED_IOS,
  DEPENDENCIES,
  PERMISSION_PERIOD,
  PERMISSION_BUSINESS_ENTITY,
  PERMISSION_AFFECTED_IOS,
  PERMISSION_AGREEMENT,
  PERMISSION_DESCRIPTION,
  PERMISSION_PAYMENT_TERMS,
  PERMISSION_BUDGET,
  PERMISSION_BUSINESS_PARTNER_CONTACT_DETAILS,
  PERMISSION_PAYMENT_DETAILS,
  PERMISSION_LEGAL_CONTACT,
  BP_EMAIL,
  GO_GAME_ACCESS_PARAGRAPH,
} from './constants';
import {
  paymentTermsValidationSchema,
  budgetValidationSchema,
  bpVersionValidationSchema,
  bpDetailsValidationSchema,
  budgetParamsSchema,
  ioValidationsSchema,
  ioApprovalValidationSchema,
} from './schemas';

const normalizeBPVersion = (_value, options) => {
  return options && options.length ? [...options].pop().id : undefined;
};

const IOForm = withForm({
  validationSchema: (_field, meta) => {
    return meta.sendForApproval
      ? ioApprovalValidationSchema
      : ioValidationsSchema;
  },
  enableReinitializeEqual: true,
})(({
  setFieldValue,
  submitForm,
  onClose,
  actions,
  values,
  dirty,
  hierarchyState,
  amendment,
  onExited,
  loading,
  open,
  ...props
}) => {
  const [bpOwnerName, setBpOwnerName] = useMountedState(null);

  const isEditDetailsAvailable = useMemo(() => {
    const bpVersionId = getIn(values, BUSINESS_PARTNER_VERSION);
    const bpVersionIdData =
      getIn(hierarchyState, `${BUSINESS_PARTNER_VERSION}.data`) || [];

    if (!bpVersionIdData.length || !bpVersionId) {
      return false;
    }
    return bpVersionIdData[bpVersionIdData.length - 1].id === bpVersionId;
  }, [values.businessPartnerVersion, hierarchyState.businessPartnerVersion]);

  const warningHierarchyIsActive = useMemo(() => {
    return Boolean(
      values.businessPartnerVersion && values.businessPartnerVersion
    );
  }, [values.businessPartnerVersion, values.businessPartnerVersion]);

  const invoiceLevel = useMemo(() => {
    return getIn(
      hierarchyState,
      `${BUSINESS_PARTNER_CONTACT_DETAILS}.data.invoiceLevel.id`
    );
  }, [hierarchyState]);

  const ios = useMemo(() => {
    return values[AFFECTED_IOS] && values[AFFECTED_IOS].length;
  }, values[AFFECTED_IOS]);

  const handleLoadBusinessPartnerDetails = ({ data }) => {
    setFieldValue(BP_EMAIL, data.defaultLegalContact.email);

    if (!data || !dirty) {
      return;
    }
    const { currency, owner, defaultLegalContact } = data;

    setFieldValue(`${BUDGET}.${CURRENCY_CODE}`, currency);
    setFieldValue(LEGAL_CONTACT, defaultLegalContact);
    setBpOwnerName(owner.name);
  };

  const handleSave = () => {
    submitForm({ sendForApproval: true });
  };

  const handleSaveAsDraft = () => {
    submitForm({ sendForApproval: false });
  };

  const handleInvoiceChangeLevel = (data) => {
    setFieldValue(AFFECTED_IOS, data);
  };

  const handleLoadIos = () => {
    if (getIn(values, AFFECTED_IOS)) {
      setFieldValue(AFFECTED_IOS, undefined);
    }
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title
        title={props.title}
        subTitle={props.name}
        entityId={props.entityId}
      />
      <Permissions actions={actions}>
        {({ isActionAllowed }) => (
          <SidePageV2.NewBody>
            <Section title={'General'}>
              <Grid container item xs={12} spacing={3}>
                <Grid item xs={4}>
                  <FormField.MonthRangePicker
                    label={'Period'}
                    name={PERIOD}
                    helper={`Max period - 12 months`}
                    disabled={!isActionAllowed(PERMISSION_PERIOD)}
                    padding={0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField.Select
                    label={'Business partner'}
                    name={BUSINESS_PARTNER}
                    source={loadBusinessPartners}
                    helper={bpOwnerName ? `Owner: ${bpOwnerName}` : ''}
                    disabled={!isActionAllowed(PERMISSION_BUSINESS_ENTITY)}
                    padding={0}
                    required={true}
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormField.Select
                    label={'BP ver.'}
                    name={BUSINESS_PARTNER_VERSION}
                    depends={DEPENDENCIES[BUSINESS_PARTNER_VERSION]}
                    source={loadBusinessPartnerVersion}
                    active={bpVersionValidationSchema.isValidSync(values)}
                    disabled={!isActionAllowed(PERMISSION_BUSINESS_ENTITY)}
                    normalizeValue={normalizeBPVersion}
                    padding={0}
                    required={true}
                    dropdownWidth={'128px'}
                  />
                </Grid>
                {!amendment ? (
                  <IOSWarningHierarchy
                    name={AFFECTED_IOS}
                    depends={DEPENDENCIES[AFFECTED_IOS]}
                    source={checkOnExistingIo}
                    onDataLoad={handleLoadIos}
                    ios={getIn(values, AFFECTED_IOS)}
                    onInvoiceChangeLevel={handleInvoiceChangeLevel}
                    active={warningHierarchyIsActive}
                  />
                ) : null}
                {hasAffiliates({ invoiceLevel, ios }) ? (
                  <Grid item xs={12}>
                    <AffiliatesField
                      invoiceLevel={invoiceLevel}
                      ios={ios}
                      disabled={!isActionAllowed(PERMISSION_BUSINESS_ENTITY)}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <FormField.TextField
                    multiline={true}
                    minRows={1}
                    name={DESCRIPTION}
                    label={'Description'}
                    disabled={!isActionAllowed(PERMISSION_DESCRIPTION)}
                    padding={0}
                  />
                </Grid>
                {ios ? (
                  <Grid item xs={12}>
                    <IOSFields
                      name={AFFECTED_IOS}
                      iosLoading={getIn(
                        hierarchyState,
                        `${AFFECTED_IOS}.loading`
                      )}
                      disabled={!isActionAllowed(PERMISSION_AFFECTED_IOS)}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={8}>
                  <Agreement
                    name={AGREEMENT}
                    disabled={!isActionAllowed(PERMISSION_AGREEMENT)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Section title={'GoGame Access Paragraph'}>
                    <FormField.RadioGroup
                      name={GO_GAME_ACCESS_PARAGRAPH}
                      radioVertical={true}
                      vertical={true}
                      padding={0}
                      source={loadParagraphIndication}
                    />
                  </Section>
                </Grid>
              </Grid>
            </Section>
            <PaymentTerms
              name={PAYMENT_TERMS}
              active={paymentTermsValidationSchema.isValidSync(values)}
              disabled={!isActionAllowed(PERMISSION_PAYMENT_TERMS)}
            />

            <Budget
              name={BUDGET}
              depends={DEPENDENCIES[BUDGET]}
              params={(values) =>
                budgetParamsSchema.cast(values, { stripUnknown: true })
              }
              source={loadBudget}
              active={budgetValidationSchema.isValidSync(values)}
              disabled={!isActionAllowed(PERMISSION_BUDGET)}
            />
            <BusinessPartnerDetails
              bpId={getIn(values, BUSINESS_PARTNER)}
              name={BUSINESS_PARTNER_CONTACT_DETAILS}
              depends={DEPENDENCIES[BUSINESS_PARTNER_CONTACT_DETAILS]}
              active={bpDetailsValidationSchema.isValidSync(values)}
              disabled={
                !isActionAllowed(PERMISSION_BUSINESS_PARTNER_CONTACT_DETAILS)
              }
              source={loadBusinessEntityDetails}
              onDataLoad={handleLoadBusinessPartnerDetails}
              isEditable={isEditDetailsAvailable}
            />

            <PaymentDetails
              name={PAYMENT_DETAILS}
              disabled={!isActionAllowed(PERMISSION_PAYMENT_DETAILS)}
            />
            <LegalContact
              name={LEGAL_CONTACT}
              disabled={!isActionAllowed(PERMISSION_LEGAL_CONTACT)}
            />
          </SidePageV2.NewBody>
        )}
      </Permissions>
      <SidePageV2.Actions>
        <Grid container justifyContent={'space-between'}>
          <Grid item>
            <Permissions actions={values.actions}>
              {({ isActionAllowed }) => {
                return isActionAllowed('SendForApproval') ||
                  isActionAllowed('AddAmendment') ? (
                  <Button color={'primary'} onClick={handleSaveAsDraft}>
                    Save as draft
                  </Button>
                ) : null;
              }}
            </Permissions>
          </Grid>
          <Grid item>
            <SidePageV2.CloseButton />
            <SidePageV2.SubmitButton
              label={'Send for approval'}
              onSubmit={handleSave}
            />
          </Grid>
        </Grid>
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default IOForm;
